import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@elit-hair/elit-ui";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons";
import { TbCheck, TbThumbDown, TbThumbDownFilled, TbThumbUp, TbThumbUpFilled, TbTool } from "react-icons/tb";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { DarwinLink } from "@/components/ai-chat/darwin-link";
import { MarkdownTable } from "@/components/ai-chat/markdown-table";
import { useSmoothStreamText } from "@/components/ai-chat/use-smooth-stream-text";
import { cn } from "@/lib/utils";
import { ChatMessageRating, ChatMessageRole, ChatMessage as ChatMessageType } from "@/types/chatbot.types";

type ChatMessageProps = {
    message: ChatMessageType;
    className?: string;
    onRate?: (params: { message: ChatMessageType; rating?: ChatMessageRating }) => void;
    onRequestTool?: (message: ChatMessageType) => void;
    isRequestingTool?: boolean;
    /** The reply is still arriving: reveal it word by word with a cursor */
    isStreaming?: boolean;
};

const RATING_BUTTONS: Array<{
    rating: ChatMessageRating;
    labelKey: string;
    icon: IconType;
    activeIcon: IconType;
    activeClassName: string;
}> = [
    {
        rating: ChatMessageRating.UP,
        labelKey: "darwin_good_response",
        icon: TbThumbUp,
        activeIcon: TbThumbUpFilled,
        activeClassName: "text-emerald-600 dark:text-emerald-400",
    },
    {
        rating: ChatMessageRating.DOWN,
        labelKey: "darwin_bad_response",
        icon: TbThumbDown,
        activeIcon: TbThumbDownFilled,
        activeClassName: "text-red-600 dark:text-red-400",
    },
];

export function ChatMessage({
    message,
    className,
    onRate,
    onRequestTool,
    isRequestingTool = false,
    isStreaming = false,
}: ChatMessageProps) {
    const { t } = useTranslation();
    const isUser = message.role === ChatMessageRole.USER;
    const shownContent = useSmoothStreamText(message.content, isStreaming);
    const isTyping = isStreaming || shownContent.length < message.content.length;

    return (
        <div className={cn("group flex items-start gap-3", isUser && "flex-row-reverse", className)}>
            {/* Message Content */}
            <div className={cn("flex-1 min-w-0", isUser && "flex flex-col items-end")}>
                <div
                    className={cn(
                        "min-w-0 rounded-2xl",
                        isTyping && "darwin-streaming",
                        isUser
                            ? "max-w-[85%] bg-gradient-to-r bg-gray-500/10 px-2 py-2 text-black dark:bg-gray-100/10 dark:text-white"
                            : "w-full text-gray-900 dark:text-white"
                    )}
                >
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                            p: ({ ...props }) => (
                                <p
                                    className="text-sm leading-relaxed break-words select-text whitespace-pre-wrap"
                                    {...props}
                                />
                            ),
                            ul: ({ ...props }) => (
                                <ul className="list-disc pl-5 my-2 text-sm leading-relaxed" {...props} />
                            ),
                            ol: ({ ...props }) => (
                                <ol className="list-decimal pl-5 my-2 text-sm leading-relaxed" {...props} />
                            ),
                            li: ({ ...props }) => <li className="mb-1" {...props} />,
                            a: ({ href, title, children }) => (
                                <DarwinLink href={href} title={title}>
                                    {children}
                                </DarwinLink>
                            ),
                            code: ({ className, children, ...props }) => (
                                <code
                                    className={cn(
                                        "rounded bg-black/5 dark:bg-white/10 px-1 py-0.5 text-[0.85em]",
                                        className
                                    )}
                                    {...props}
                                >
                                    {children}
                                </code>
                            ),
                            pre: ({ ...props }) => (
                                <pre
                                    className="whitespace-pre-wrap text-sm leading-relaxed font-mono bg-black/5 dark:bg-white/10 rounded-md p-3 my-2 overflow-x-auto"
                                    {...props}
                                />
                            ),
                            table: ({ children }) => <MarkdownTable>{children}</MarkdownTable>,
                            thead: ({ children }) => <thead className="bg-muted/60">{children}</thead>,
                            tr: ({ children }) => (
                                <tr className="border-b border-border last:border-b-0">{children}</tr>
                            ),
                            th: ({ children, style }) => (
                                <th
                                    style={style}
                                    className="whitespace-nowrap px-3 py-1.5 text-left align-middle text-xs font-medium text-muted-foreground [&_p]:whitespace-nowrap"
                                >
                                    {children}
                                </th>
                            ),
                            td: ({ children, style }) => (
                                <td
                                    style={style}
                                    className="whitespace-nowrap px-3 py-1.5 text-left align-middle tabular-nums [&_p]:whitespace-nowrap"
                                >
                                    {children}
                                </td>
                            ),
                        }}
                    >
                        {shownContent}
                    </ReactMarkdown>
                </div>

                {!isUser && message.toolRequest && !isTyping && message.toolRequest.incidentId ? (
                    <Button variant="outline" size="sm" disabled className="mt-2 h-7 gap-1.5 px-2 text-xs">
                        <TbCheck className="size-3.5" />
                        {t("darwin_tool_requested")}
                    </Button>
                ) : null}
                {!isUser && message.toolRequest && onRequestTool && !isTyping && !message.toolRequest.incidentId ? (
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={isRequestingTool}
                        onClick={() => onRequestTool(message)}
                        className="mt-2 h-7 gap-1.5 px-2 text-xs"
                    >
                        <TbTool className="size-3.5" />
                        {t("darwin_request_tool")}
                    </Button>
                ) : null}

                {/* Timestamp and rating */}
                <div className={cn("flex items-center gap-2 mt-1", isUser ? "justify-end" : "justify-start")}>
                    <div
                        className={cn(
                            "text-xs text-gray-500 dark:text-white/50 select-text",
                            isUser ? "text-right" : "text-left"
                        )}
                    >
                        {message.createdAt
                            ? DateTime.fromISO(message.createdAt).toLocaleString(DateTime.TIME_SIMPLE)
                            : ""}
                    </div>

                    {!isUser && onRate && message.content && !isTyping && (
                        <div
                            className={cn(
                                "flex items-center gap-0.5 transition-opacity duration-200",
                                message.rating
                                    ? "opacity-100"
                                    : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"
                            )}
                        >
                            {RATING_BUTTONS.map(({ rating, labelKey, icon, activeIcon, activeClassName }) => {
                                const isActive = message.rating === rating;
                                const RatingIcon = isActive ? activeIcon : icon;

                                return (
                                    <Tooltip key={rating}>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                aria-label={t(labelKey)}
                                                aria-pressed={isActive}
                                                onClick={() =>
                                                    onRate({ message, rating: isActive ? undefined : rating })
                                                }
                                                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                                            >
                                                <RatingIcon className={cn("size-4", isActive && activeClassName)} />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>{t(labelKey)}</TooltipContent>
                                    </Tooltip>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
